import { useCallback, useEffect, useMemo, useState } from 'react';

import { useClickhouseMediaAnalytics } from '@/utils/hooks/featureFlag/useClickhouseMediaAnalytics';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import useMedia from '@/utils/hooks/swr/useMedia';

import type { SocialProfileNetworkType, SocialProfileType } from '@/types/socialProfileType';

import { POST, STORY } from '@/lib/posts/postTypes';
import { INSTAGRAM } from '@/lib/socialProfile';

const PREFETCH_THRESHOLD = 3;

interface NavMedia {
    id: string;
    social_profile_id?: number;
}

interface UseMediaViewerNavigationParams {
    mediaId?: string;
    mediasList?: NavMedia[];
    mediaType?: string;
    group?: SpGroupType | null;
    socialProfile?: SocialProfileType | null;
    mediaRequestParams?: Record<string, unknown>;
    onLoadMore?: () => void;
    hasMore?: boolean;
    isLoadingMore?: boolean;
}

interface CurrentContentState {
    mediaId?: string;
    socialProfile?: SocialProfileType | null;
}

interface UseMediaViewerNavigationReturn {
    media: unknown;
    previousMedia: NavMedia | null;
    nextMedia: NavMedia | null;
    currentContentState: CurrentContentState;
    currentNetworkType: SocialProfileNetworkType;
    isLoading: boolean;
    mutateMedia: (data?: unknown) => Promise<unknown>;
    goToMedia: (navMedia: NavMedia | null) => void;
    getNavMediaNetworkType: (navMedia?: NavMedia | null) => SocialProfileNetworkType;
    useClientNav: boolean;
    hasNavigation: boolean;
    showNextNav: boolean;
    isNextNavDisabled: boolean;
}

const useMediaViewerNavigation = ({
    mediaId,
    mediasList = [],
    mediaType = POST,
    group,
    socialProfile,
    mediaRequestParams = {},
    onLoadMore,
    hasMore = false,
    isLoadingMore = false
}: UseMediaViewerNavigationParams): UseMediaViewerNavigationReturn => {
    const { isActiveForNetwork } = useClickhouseMediaAnalytics();
    const useClientNav =
        isActiveForNetwork(socialProfile?.social_network_type) && !group && mediaType !== STORY;

    const [currentContentState, setCurrentContentState] = useState({
        mediaId: mediaId,
        socialProfile: socialProfile
    });

    useEffect(() => {
        setCurrentContentState((prevState) => ({
            ...prevState,
            mediaId: mediaId,
            socialProfile: socialProfile ?? prevState.socialProfile
        }));
    }, [mediaId, socialProfile]);

    const currentIndex = useMemo(
        () =>
            useClientNav ? mediasList.findIndex((m) => m.id === currentContentState.mediaId) : -1,
        [useClientNav, mediasList, currentContentState.mediaId]
    );

    const clientPreviousMedia = currentIndex > 0 ? mediasList[currentIndex - 1] : null;
    const clientNextMedia =
        currentIndex >= 0 && currentIndex < mediasList.length - 1
            ? mediasList[currentIndex + 1]
            : null;

    const handleLoadMore = useCallback(() => {
        if (hasMore && !isLoadingMore && onLoadMore) {
            onLoadMore();
        }
    }, [hasMore, isLoadingMore, onLoadMore]);

    useEffect(() => {
        if (!useClientNav || currentIndex < 0) return;

        const distanceToEnd = mediasList.length - 1 - currentIndex;
        if (distanceToEnd <= PREFETCH_THRESHOLD) {
            handleLoadMore();
        }
    }, [useClientNav, currentIndex, mediasList.length, handleLoadMore]);

    const {
        media,
        previousMedia: serverPreviousMedia,
        nextMedia: serverNextMedia,
        isLoading,
        mutateMedia
    } = useMedia({
        groupID: group?.id,
        spID: useClientNav
            ? currentContentState.socialProfile?.id ?? socialProfile?.id
            : socialProfile?.id,
        mediaID: currentContentState.mediaId,
        socialNetworkType: useClientNav
            ? currentContentState.socialProfile?.social_network_type ??
              socialProfile?.social_network_type
            : socialProfile?.social_network_type,
        type: mediaType,
        requestParams: mediaRequestParams,
        withPrevNext: !useClientNav
    });

    const previousMedia = useClientNav ? clientPreviousMedia : serverPreviousMedia;
    const nextMedia = useClientNav ? clientNextMedia : serverNextMedia;
    const hasNavigation = useClientNav ? mediasList.length > 0 : true;

    const goToMedia = (navMedia: NavMedia | null) => {
        if (!navMedia || isLoading) return;

        setCurrentContentState((prevState) => ({
            ...prevState,
            mediaId: navMedia.id,
            socialProfile: group
                ? group.social_profiles.find((sp) => sp.id === navMedia.social_profile_id)
                : socialProfile
        }));
    };

    const getNavMediaNetworkType = (navMedia?: NavMedia | null): SocialProfileNetworkType => {
        const foundProfile = group
            ? group.social_profiles.find((sp) => sp.id === navMedia?.social_profile_id)
            : undefined;
        const profile = foundProfile ?? currentContentState.socialProfile;
        return profile?.social_network_type ?? INSTAGRAM;
    };

    useEffect(() => {
        if (!group || !media) return;

        setCurrentContentState((prevState) => ({
            ...prevState,
            socialProfile: group.social_profiles.find((sp) => sp.id === media.social_profile_id)
        }));
    }, [group, media]);

    const currentNetworkType = currentContentState.socialProfile?.social_network_type ?? INSTAGRAM;
    const showNextNav = useClientNav ? nextMedia || hasMore : !!nextMedia;
    const isNextNavDisabled = useClientNav && !nextMedia && hasMore;

    return {
        media,
        previousMedia,
        nextMedia,
        currentContentState,
        currentNetworkType,
        isLoading,
        mutateMedia,
        goToMedia,
        getNavMediaNetworkType,
        useClientNav,
        hasNavigation,
        showNextNav,
        isNextNavDisabled
    };
};

export default useMediaViewerNavigation;
